import { BucketControllerApiFactory, CustomerControllerV3ApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import MainBlock from "$root/components/EvolvedPrint/configuration";
import useExtractReportsData from "$root/components/EvolvedPrint/configuration/hooks/useExtractReports";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import type { Languages } from "$root/localization/i18n";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { parallelize } from "$root/utils";
import { CircularProgressBar } from "@mdotm/shadcn/legacy/components";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { lazy, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { CustomReportEditorContext, initCustomEditorContextData } from "./context";
import "./print.css";
import { useQuery } from "@tanstack/react-query";
import { userRepository } from "$root/functional-areas/user/repository";

const Theme = lazy(() => import("../../components/EvolvedPrint/layout/portrait"));

const CustomReportEditor = (): React.JSX.Element => {
	const { customerId } = useParams<{ customerId: string }>();
	const customerApi = useApiGen(CustomerControllerV3ApiFactory);
	const bucketApi = useApiGen(BucketControllerApiFactory);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { i18n } = useTranslation();

	const queryCustomerList = useQuery({
		...userRepository.customerList.getQueryConfig(),
		enabled: customerId != null,
	});

	const customerList = queryCustomerList.data;

	async function maybeUploadToBucket(file: File | null | undefined) {
		if (!file) {
			return undefined;
		}
		return await axiosExtract(bucketApi.uploadFile(file));
	}

	const [contextContent, setContextContent] = useState(initCustomEditorContextData);

	const {
		state: { data: fetchedData, isLoading },
		refetch,
	} = useExtractReportsData({ template: "mocked", language: i18n.language as Languages });

	const data = useMemo(
		() =>
			fetchedData &&
			({
				...fetchedData,
				payload: {
					...fetchedData.payload,
					report: {
						...fetchedData.payload.report,
						title: "PORTFOLIO DETAILS",
						subtitle: "NAME REFERENCE",
						address: contextContent.texts.address ?? fetchedData.payload.report.address,
						disclaimer: contextContent.texts.disclaimer ?? fetchedData.payload.report.disclaimer,
						website: contextContent.texts.website ?? fetchedData.payload.report.website,
						email: contextContent.texts.email ?? fetchedData.payload.report.email,
					},
				},
			} as typeof fetchedData),
		[
			contextContent.texts.address,
			contextContent.texts.disclaimer,
			contextContent.texts.email,
			contextContent.texts.website,
			fetchedData,
		],
	);

	const [forceRemount, setForceRemount] = useState(0);
	async function resetCustomFields() {
		const doReset = await spawnYesNoDialog();
		if (doReset) {
			setContextContent(initCustomEditorContextData());
			setForceRemount((n) => n + 1);
		}
	}

	async function onSubmit() {
		try {
			setIsSubmitting(true);
			const customer = customerList?.find((x) => x.id === customerId);
			if (!customer) {
				alert("unable to find a valid customer");
				throw new Error("unable to find a valid customer");
			}

			const [backgroundImage = customer.backgroundImage, clientLogo = customer.clientLogo] = await parallelize(
				[
					() => maybeUploadToBucket(contextContent.files.backgroundImage),
					() => maybeUploadToBucket(contextContent.files.clientLogo),
				],
				{ concurrency: 2 },
			);

			const {
				disclaimer = customer.disclaimer,
				address = customer.address,
				email = customer.email,
				website = customer.website,
			} = contextContent.texts;

			await customerApi.updateCustomer({
				...customer,
				backgroundImage,
				clientLogo,
				disclaimer,
				email,
				website,
				address,
			});
			await refetch();
			setForceRemount((n) => n + 1);
			window.close();
		} catch (error) {
			console.log(error);
			platformToast({
				icon: "Icon-full-error",
				children: "Si è verificato un errore",
				severity: "error",
			});
		} finally {
			setIsSubmitting(false);
		}
	}

	if (!data || isLoading || isSubmitting) {
		return (
			<Theme>
				<div data-size="A4portrait" className="page_brake">
					<div className="h-full flex justify-center items-center">
						<CircularProgressBar value="indeterminate" />
					</div>
				</div>
			</Theme>
		);
	}

	return (
		<CustomReportEditorContext.Provider
			key={forceRemount}
			value={{
				value: contextContent,
				set: setContextContent,
				update: setContextContent,
			}}
		>
			<Theme>
				<section className="indicators">
					<div data-size="A4Template">
						<MainBlock {...data} layout="portrait" />
					</div>
				</section>

				<div className="fixed bottom-8 left-14 z-50">
					<Button
						unstyled
						className="rounded-full h-12 w-12 shadow-md active:scale-90 transition-transform font-semibold text-white hover:text-slate-800"
						style={{ backgroundColor: themeCSSVars.palette_D200 }}
						onClickAsync={resetCustomFields}
					>
						Reset
					</Button>
				</div>

				<div className="fixed bottom-8 right-14 z-50">
					<Button
						unstyled
						className="rounded-full h-12 w-12 shadow-md active:scale-90 transition-transform font-semibold text-white hover:text-slate-800"
						style={{ backgroundColor: themeCSSVars.palette_P500 }}
						onClickAsync={onSubmit}
					>
						Save
					</Button>
				</div>
			</Theme>
		</CustomReportEditorContext.Provider>
	);
};

export default CustomReportEditor;
